import { useGetDayjsLocalizedCalendarLabels, useLocalizationContext } from '@va/localization';
import { inWebsiteTimezone } from '@va/util/helpers';
import { Chart } from 'chart.js';
import { Dayjs } from 'dayjs';
import { isNil } from 'lodash';
import { ReactNode, useCallback } from 'react';
import { IndicatorLinesArgs, TooltipHeaderArgs } from './ExternalChartTooltip';
import { chartIncludesToday } from './chartIntervals';
import { LiveDataLine } from './common/LiveDataLine';
import { LiveTooltipHeader } from './common/LiveTooltipHeader';

/** Approximate Live chip footprint in CSS px; sets the horizontal/vertical buffers checked around it. */
const CHIP_WIDTH_PX = 60;
const CHIP_HEIGHT_PX = 25;

function topMaxNearLastPoint(chart: Chart, lastDataIndex: number): boolean {
  const minMaxValues = chart.$minMaxValues;
  if (lastDataIndex < 0 || !minMaxValues?.length) return false;

  const { datasets } = chart.data;
  // Hidden datasets (toggled-off KPIs, disabled comparison) draw no label, so ignore them.
  const isHidden = (datasetIndex: number) => !!datasets[datasetIndex]?.hidden;

  const overallMax = Math.max(
    ...minMaxValues.filter((_, datasetIndex) => !isHidden(datasetIndex)).map((value) => value.maxValue),
    1,
  );
  if (!Number.isFinite(overallMax)) return false;

  const valueAtChipBottom = chart.scales['y']?.getValueForPixel(CHIP_HEIGHT_PX);
  const topValueThreshold = !isNil(valueAtChipBottom) ? Math.min(overallMax, valueAtChipBottom) : overallMax;

  const totalDataPoints = lastDataIndex + 1;
  const canvasWidth = chart.canvas.clientWidth || chart.width;
  const pixelsPerPoint = canvasWidth / totalDataPoints;
  const checkedCount = pixelsPerPoint > 0 ? Math.max(1, Math.ceil(CHIP_WIDTH_PX / pixelsPerPoint)) : 1;
  const firstCheckedIndex = Math.max(0, totalDataPoints - checkedCount);

  return datasets.some((dataset, datasetIndex) => {
    const maxValue = minMaxValues[datasetIndex]?.maxValue;
    // Skip hidden datasets and those whose max label sits too low to reach the chip.
    if (isHidden(datasetIndex) || isNil(maxValue) || maxValue < topValueThreshold) return false;
    for (let index = firstCheckedIndex; index <= lastDataIndex; index++) {
      if ((dataset.data as number[])[index] === maxValue) return true;
    }
    return false;
  });
}

export const useLiveChartRenderers = ({
  lastVisitDate,
  isCurrentlyLimitReached = false,
}: {
  lastVisitDate?: Dayjs;
  isCurrentlyLimitReached?: boolean;
}) => {
  const { translate, locale } = useLocalizationContext();
  const calendarLabels = useGetDayjsLocalizedCalendarLabels();

  const renderLiveLine = useCallback(
    ({ chart, currentTimeIntervals, offsetLeft, lastDataIndex }: IndicatorLinesArgs): ReactNode => {
      if (!chartIncludesToday(currentTimeIntervals) || isCurrentlyLimitReached || lastDataIndex < 0) return null;

      const lastPointX = chart.scales['x']?.getPixelForValue(lastDataIndex);
      if (isNil(lastPointX) || Number.isNaN(lastPointX)) return null;

      const labelAtLastPoint =
        (!!chart.$showDataLabels || !!chart.$highlightMinMax) && topMaxNearLastPoint(chart, lastDataIndex);

      return (
        <LiveDataLine
          chart={chart}
          dataIndex={lastDataIndex}
          left={offsetLeft + lastPointX}
          chipLabel={translate('dashboard.lineChart.liveChip')}
          hideChip={labelAtLastPoint}
        />
      );
    },
    [isCurrentlyLimitReached, translate],
  );

  const renderLiveHeader = useCallback(
    ({ currentTimeIntervals, dataPoints, lastDataIndex }: TooltipHeaderArgs): ReactNode => {
      const isLiveDataPoint =
        chartIncludesToday(currentTimeIntervals) &&
        !isCurrentlyLimitReached &&
        lastDataIndex >= 0 &&
        dataPoints?.[0]?.dataIndex === lastDataIndex;

      if (!isLiveDataPoint) return null;

      return (
        <LiveTooltipHeader
          chipLabel={translate('dashboard.lineChart.tooltip.liveTitle', {
            date: lastVisitDate
              ? inWebsiteTimezone(lastVisitDate).locale(locale).calendar(null, calendarLabels)
              : translate('all.notApplicable'),
          })}
        />
      );
    },
    [calendarLabels, isCurrentlyLimitReached, lastVisitDate, locale, translate],
  );

  return { renderLiveLine, renderLiveHeader };
};
